export const OPS_ORB_CHUNK_RECOVERY_COOLDOWN_MS = 30_000;

const OPS_ORB_CHUNK_RECOVERY_KEY = "business-ops:orb-chunk-recovery-at";

export type OpsOrbChunkRecoveryHost = {
  addEventListener(type: "vite:preloadError", listener: EventListener): void;
  removeEventListener(type: "vite:preloadError", listener: EventListener): void;
  sessionStorage: Pick<Storage, "getItem" | "setItem">;
  location: Pick<Location, "reload">;
};

export function installOpsOrbChunkRecovery(
  suppliedHost?: OpsOrbChunkRecoveryHost,
  now: () => number = Date.now
): () => void {
  const host = suppliedHost ?? {
    addEventListener(type: "vite:preloadError", listener: EventListener) {
      window.addEventListener(type, listener);
    },
    removeEventListener(type: "vite:preloadError", listener: EventListener) {
      window.removeEventListener(type, listener);
    },
    sessionStorage: window.sessionStorage,
    location: window.location
  };
  const onPreloadError: EventListener = (event) => {
    // WHY [BOL-ORB-CHUNK-VERSION-015]: a live Vite document can outlast a
    // deployment and request its old lazy Orb chunk after that chunk was removed.
    // That exact version skew rendered only the empty unavailable outline on the
    // Samsung. Keep old assets during publication, and make a single bounded
    // no-cache HTML reload the fail-safe. Never loop or substitute another Orb.
    event.preventDefault();
    let previous: number | null = null;
    const current = now();
    try {
      const raw = host.sessionStorage.getItem(OPS_ORB_CHUNK_RECOVERY_KEY);
      previous = raw === null ? null : Number(raw);
      if (previous !== null && Number.isFinite(previous)
        && current - previous < OPS_ORB_CHUNK_RECOVERY_COOLDOWN_MS) return;
      host.sessionStorage.setItem(OPS_ORB_CHUNK_RECOVERY_KEY, String(current));
    } catch {
      // Storage is the cross-reload loop fence. Without it, fail closed through
      // the component's unavailable state instead of risking a reload loop.
      return;
    }
    host.location.reload();
  };
  host.addEventListener("vite:preloadError", onPreloadError);
  return () => host.removeEventListener("vite:preloadError", onPreloadError);
}
